import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Check, Music } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { MaskedLines, FadeUp, SectionTag } from "@/components/Reveal";
import { CEREMONIES, mailto } from "@/data/content";

const Zeremonien = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <div data-testid="zeremonien-page">
      <SEO
        title="Zeremonien – Freie Trauung, Willkommensfeier & Jubiläum | Daviz & Puxi"
        description="Alle Zeremonien im Überblick: freie Trauung, Hochzeitszeremonie, Willkommensfeier, Eheversprechen erneuern, Jubiläum und Lebensfeier – in Krefeld, NRW & deutschlandweit."
        path="/zeremonien"
      />

      <section className="mx-auto max-w-7xl px-5 pb-20 pt-36 sm:px-8 sm:pt-44">
        <FadeUp>
          <SectionTag>Zeremonien</SectionTag>
        </FadeUp>
        <MaskedLines
          lines={["Für Anfänge, Versprechen", "und große Feste."]}
          className="mt-6 font-serif text-5xl leading-[1.05] sm:text-7xl"
          delay={0.15}
        />
        <FadeUp delay={0.5}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-stone">
            Jede Zeremonie, die wir gestalten, ist ein Unikat. Hier finden Sie einen Überblick –
            und für jeden Anlass einen direkten Weg zu uns. Sie planen Ihre Hochzeit in Krefeld
            und Umgebung? Dann schauen Sie gern auf unsere Seite zur{" "}
            <Link to="/freie-trauung-krefeld" data-testid="zeremonien-link-krefeld" className="font-semibold text-rust underline decoration-rust/40 underline-offset-4 transition-colors hover:text-moss">
              freien Trauung in Krefeld
            </Link>.
          </p>
        </FadeUp>

        <FadeUp delay={0.6} className="mt-10 flex flex-wrap gap-3">
          {CEREMONIES.map((c) => (
            <a
              key={c.slug}
              href={`#${c.slug}`}
              data-testid={`zeremonie-anchor-${c.slug}`}
              className="rounded-full border border-sand px-5 py-2 text-xs font-semibold text-stone transition-colors hover:border-moss hover:bg-moss hover:text-creme"
            >
              {c.title}
            </a>
          ))}
        </FadeUp>
      </section>

      <div className="mx-auto max-w-7xl space-y-28 px-5 pb-24 sm:px-8">
        {CEREMONIES.map((c, i) => (
          <FadeUp key={c.slug}>
            <article
              id={c.slug}
              data-testid={`zeremonie-${c.slug}`}
              className={`grid scroll-mt-28 items-center gap-10 lg:grid-cols-12`}
            >
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="group relative overflow-hidden border border-sand">
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
                  />
                  <span className="absolute left-5 top-5 rounded-full bg-creme/90 px-4 py-1.5 font-accent text-lg italic text-moss backdrop-blur">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              </div>
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-8"}`}>
                <h2 className="font-serif text-3xl sm:text-4xl">{c.title}</h2>
                <p className="mt-3 font-accent text-xl italic text-rust">{c.teaser}</p>
                <p className="mt-5 leading-relaxed text-stone">{c.description}</p>

                <div className="mt-6 space-y-4 border-t border-sand/70 pt-6 text-sm">
                  <p><span className="font-semibold text-ink">Für wen: </span><span className="text-stone">{c.forWhom}</span></p>
                  <p><span className="font-semibold text-ink">Unsere Rolle: </span><span className="text-stone">{c.role}</span></p>
                </div>

                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rust">Der ungefähre Ablauf</p>
                  <ol className="mt-3 space-y-2">
                    {c.ablauf.map((step, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-stone">
                        <Check size={14} className="shrink-0 text-moss" /> {step}
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="mt-6 flex items-start gap-4 border border-rust/40 bg-parch/60 p-5">
                  <Music size={20} className="mt-0.5 shrink-0 text-rust" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rust">Unser besonderes Extra</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-ink">
                      <span className="font-semibold">Ihr Lied – live performt:</span> Mark Daviz singt Ihren Song live zur Zeremonie, Angela Puxi spielt Ihr Lied live mit Saxophon zur Zeremonie. Auch kann der jeweils andere dazugebucht werden und komplettiert Ihre Feier zu einem unvergesslichen Moment. Kein Playback, keine Konserve – Ihre Geschichte und Ihr Lied, live.
                    </p>
                  </div>
                </div>

                <a
                  href={mailto(`Anfrage: ${c.title}`)}
                  data-testid={`zeremonie-anfrage-${c.slug}`}
                  className="mt-8 inline-flex items-center gap-3 rounded-full bg-moss px-7 py-3.5 text-sm font-bold text-creme transition-all duration-300 hover:scale-[1.03] hover:bg-mossdeep"
                >
                  {c.title} anfragen <ArrowRight size={15} />
                </a>
              </div>
            </article>
          </FadeUp>
        ))}
      </div>

      <CTASection
        title={["Ihr Anlass war", "nicht dabei?"]}
        text="Umso besser. Wir lieben Anlässe, für die es noch keinen Namen gibt. Erzählen Sie uns davon – gemeinsam finden wir die Form."
        buttonLabel="Individuelle Zeremonie anfragen"
        subject="Anfrage: Individuelle Zeremonie"
      />
    </div>
  );
};

export default Zeremonien;
